import { ApplicationConfig, importProvidersFrom, Injector, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { serverErrorInterceptor } from './core/server-error.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';

// Firebase
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';

// Third-party modules
import { NgbModule, NgbActiveModal, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import {
  NgxUiLoaderModule,
  NgxUiLoaderConfig,
  SPINNER,
  POSITION,
  PB_DIRECTION,
  NgxUiLoaderRouterModule,
  NgxUiLoaderHttpModule
} from 'ngx-ui-loader';
import { ClipboardModule } from 'ngx-clipboard';
import { ColorPickerModule } from 'ngx-color-picker';
import { DataTablesModule } from 'angular-datatables';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxFilesizeModule } from 'ngx-filesize';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Services
import { AuthserviceService } from './services/authservice.service';
import { ApiserviceService } from './services/apiservice.service';
import { GlobalTranslateService } from './services/global-translate.service';
import { EnglishDatepickerI18n } from './services/english-datepicker-i18n.service';
import { CookieService } from 'ngx-cookie-service';

// Environment
import { environment } from '../environments/environment';

// Translation loader factory
// Cache-busted per page load (query param varies) so a stale cached en.json/jp.json
// from a browser's HTTP cache can't leave some strings untranslated after a language switch.
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?v=' + Date.now());
}

// App initializer factory
function appInitializerFactory(translateService: TranslateService) {
  return () => {
    return translateService.use('jp').toPromise();
  };
}

// NgxUiLoader configuration
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: 'red',
  bgsOpacity: 0.5,
  bgsPosition: POSITION.bottomLeft,
  bgsSize: 60,
  bgsType: SPINNER.ballScaleMultiple,
  blur: 5,
  delay: 0,
  fastFadeOut: true,
  fgsColor: '##6f6e6e',
  fgsPosition: POSITION.centerCenter,
  fgsSize: 140,
  fgsType: SPINNER.ballScaleMultiple,
  gap: 24,
  logoPosition: POSITION.centerCenter,
  logoSize: 120,
  overlayBorderRadius: '0',
  overlayColor: 'rgba(40, 40, 40, 0.8)',
  pbColor: '#2196f3',
  pbDirection: PB_DIRECTION.leftToRight,
  pbThickness: 5,
  hasProgressBar: true,
  textColor: '#FFFFFF',
  textPosition: POSITION.centerCenter,
  maxTime: -1,
  minTime: 500
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([serverErrorInterceptor]), withInterceptorsFromDi()),
    provideAnimations(),

    // Firebase providers
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },

    // Import modules that use forRoot
    importProvidersFrom(
      NgbModule,
      FormsModule,
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpTranslateLoader,
          deps: [HttpClient]
        }
      }),
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
        timeOut: 7000,
        progressBar: true
      }),
      NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
      NgxUiLoaderRouterModule,
      NgxUiLoaderHttpModule,
      ClipboardModule,
      ColorPickerModule,
      DataTablesModule,
      NgOtpInputModule,
      NgxPaginationModule,
      NgxFilesizeModule,
      AngularFireStorageModule
    ),

    // App initializer
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    },

    // Services
    AuthserviceService,
    ApiserviceService,
    TranslateService,
    DatePipe,
    GlobalTranslateService,
    NgbActiveModal,
    CookieService,

    // DatePicker Configuration - Force English locale for all datepickers
    { provide: NgbDatepickerI18n, useClass: EnglishDatepickerI18n },

    // Location strategy
    { provide: LocationStrategy, useClass: HashLocationStrategy }
  ]
};
